/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * R8 (KDS-QSR-19): consumo de modifiers por modifier/producto/estación +
 * reconciliación ledger vs stock_movements. Se auto-carga con el rango del
 * período (mismo patrón que ChannelBreakdownSection).
 *
 * Drill-down (follow-up R8): cada fila agregada se expande y llama a
 * get_modifier_consumption_detail(linked_product_id) para auditar el ledger
 * hasta la venta origen (sale_id → detalle de venta). Filas sin insumo
 * vinculado expanden las entradas 'unlinked' del ledger.
 */
import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ReportsDenseListCard } from './ReportsDenseListCard';
import { ReportsBlockHeading } from './ReportsBlockHeading';
import { ReportsLoadErrorRow } from './ReportsLoadErrorRow';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { supabase } from '../../../shared/supabase/supabaseClient';
import { navigateInShell } from '../../../shell/navigation/rootNavigation';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { exportModifierConsumptionCsv } from '../reportsCsvExport';
import { showError } from '../../../shared/ui/appToast';

type ConsumptionRow = {
  item_product_id: string | null;
  item_product_name: string | null;
  choice_id: string | null;
  choice_name: string | null;
  group_name: string | null;
  modifier_kind: string | null;
  linked_product_id: string | null;
  linked_product_name: string | null;
  station_id: string | null;
  station_name: string | null;
  consumed_qty: number | null;
  unlinked_qty: number | null;
  reverted_qty: number | null;
  sales_count: number | null;
};

type ReconciliationRow = {
  linked_product_id: string | null;
  linked_product_name: string | null;
  ledger_qty: number | null;
  moved_qty: number | null;
  diff: number | null;
};

type Report = {
  rows: ConsumptionRow[];
  reconciliation: ReconciliationRow[];
  unlinked_count: number;
};

/** Fila del ledger (RPC get_modifier_consumption_detail, migración 20260920293000). */
type DetailRow = {
  id: string | null;
  sale_id: string | null;
  item_product_name: string | null;
  group_name: string | null;
  choice_name: string | null;
  qty: number | null;
  status: string | null;
  station_name: string | null;
  stock_movement_id: string | null;
  created_at: string | null;
  reversed_at: string | null;
};

type DetailState = {
  loading: boolean;
  error: boolean;
  totalCount: number;
  truncated: boolean;
  rows: DetailRow[];
};

function formatQty(n: number | null | undefined): string {
  const v = n ?? 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '');
}

function statusLabel(status: string | null): string {
  switch (status) {
    case 'consumed':
      return 'consumido';
    case 'reverted':
      return 'revertido';
    case 'unlinked':
      return 'sin insumo';
    default:
      return status ?? '—';
  }
}

export function ReportsModifierConsumptionSection({ from, to }: { from: string; to: string }) {
  const navigation = useNavigation<any>();
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, DetailState>>({});

  useEffect(() => {
    if (!currentBranchId) return;
    setLoading(true);
    setLoadError(false);
    setExpandedKey(null);
    setDetails({});
    Promise.resolve(
      supabase
        .rpc('get_modifier_consumption_report', {
          p_branch_id: currentBranchId,
          p_date_from: from,
          p_date_to: to,
        })
        .then(({ data, error }) => {
          if (error) {
            console.warn('ReportsModifierConsumptionSection', error);
            setReport(null);
            setLoadError(true);
          } else {
            setReport((data ?? null) as Report | null);
          }
        }),
    ).finally(() => setLoading(false));
  }, [currentBranchId, from, to, reloadTick]);

  if (!currentBranchId) return null;

  const rows = report?.rows ?? [];
  const reconciliation = report?.reconciliation ?? [];
  const offCount = reconciliation.filter((r) => (r.diff ?? 0) !== 0).length;

  const loadDetail = (key: string, linkedProductId: string | null) => {
    if (details[key]?.loading) return;
    setDetails((prev) => ({
      ...prev,
      [key]: { loading: true, error: false, totalCount: 0, truncated: false, rows: [] },
    }));
    void supabase
      .rpc('get_modifier_consumption_detail', {
        p_branch_id: currentBranchId,
        p_linked_product_id: linkedProductId,
        p_date_from: from,
        p_date_to: to,
      })
      .then(({ data, error }) => {
        if (error) {
          console.warn('ReportsModifierConsumptionSection detail', error);
          setDetails((prev) => ({
            ...prev,
            [key]: { loading: false, error: true, totalCount: 0, truncated: false, rows: [] },
          }));
          return;
        }
        const d = (data ?? {}) as {
          rows?: DetailRow[];
          total_count?: number;
          truncated?: boolean;
        };
        setDetails((prev) => ({
          ...prev,
          [key]: {
            loading: false,
            error: false,
            totalCount: Number(d.total_count ?? 0),
            truncated: d.truncated === true,
            rows: Array.isArray(d.rows) ? d.rows : [],
          },
        }));
      });
  };

  const toggleRow = (key: string, linkedProductId: string | null) => {
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (!details[key]) loadDetail(key, linkedProductId);
  };

  const openSale = (saleId: string | null) => {
    if (!saleId) return;
    navigateInShell(navigation, 'SaleDetail', { saleId });
  };

  const rowKey = (row: ConsumptionRow, idx: number) =>
    `${row.item_product_id ?? 'x'}|${row.choice_id ?? 'x'}|${row.station_id ?? 'none'}|${idx}`;

  const renderDetail = (key: string, linkedProductId: string | null) => {
    const d = details[key];
    if (!d || d.loading) {
      return <Text style={[styles.caption, { paddingVertical: spacing.xs }]}>Cargando ventas…</Text>;
    }
    if (d.error) {
      return (
        <TouchableOpacity
          onPress={() => loadDetail(key, linkedProductId)}
          accessibilityRole="button"
          accessibilityLabel="Reintentar carga del detalle"
        >
          <Text style={[styles.caption, { color: '#d32f2f', paddingVertical: spacing.xs }]}>
            No se pudo cargar el detalle. Reintentar.
          </Text>
        </TouchableOpacity>
      );
    }
    if (d.rows.length === 0) {
      return <Text style={[styles.caption, { paddingVertical: spacing.xs }]}>Sin movimientos en el período.</Text>;
    }
    return (
      <View style={{ paddingBottom: spacing.xs }}>
        {d.rows.map((dr, i) => (
          <TouchableOpacity
            key={dr.id ?? i}
            disabled={!dr.sale_id}
            onPress={() => openSale(dr.sale_id)}
            accessibilityRole={dr.sale_id ? 'button' : undefined}
            accessibilityLabel={
              dr.sale_id
                ? `Venta ${dr.sale_id.slice(0, 8)}, ${dr.choice_name ?? 'opción'}, ${formatQty(dr.qty)}`
                : undefined
            }
            style={[
              styles.reportsDenseListFieldRow,
              i > 0 && {
                borderTopWidth: StyleSheet.hairlineWidth,
                borderTopColor: ticketColors.rule,
                paddingTop: spacing.xs,
              },
            ]}
          >
            <View style={{ flex: 1 }}>
              <Text style={styles.reportsDenseListFieldLabel} numberOfLines={1}>
                {dr.sale_id ? `Venta #${dr.sale_id.slice(0, 8)}` : 'Sin venta asociada'}
                {dr.item_product_name ? ` · ${dr.item_product_name}` : ''}
              </Text>
              <Text style={styles.caption} numberOfLines={2}>
                {formatLocalDateTime(dr.created_at)} · {statusLabel(dr.status)}
                {dr.reversed_at ? ` · revertido ${formatLocalDateTime(dr.reversed_at)}` : ''}
                {dr.station_name ? ` · ${dr.station_name}` : ''}
              </Text>
            </View>
            <Text style={styles.reportsDenseListFieldValue}>{formatQty(dr.qty)}</Text>
          </TouchableOpacity>
        ))}
        {d.truncated ? (
          <Text style={[styles.caption, { marginTop: spacing.xs }]}>
            Mostrando las {d.rows.length} más recientes de {d.totalCount}.
          </Text>
        ) : null}
      </View>
    );
  };

  return (
    <View style={{ marginTop: spacing.md }}>
      <ReportsBlockHeading
        label="Consumo de adicionales"
        description="Qué opciones de menú consumieron stock en el período. Cada fila muestra las ventas."
      />
      <ReportsDenseListCard>
        {loading ? (
          <Text style={styles.caption}>Cargando…</Text>
        ) : loadError ? (
          <ReportsLoadErrorRow
            message="No se pudo cargar el consumo de adicionales."
            onRetry={() => setReloadTick((t) => t + 1)}
          />
        ) : rows.length > 0 ? (
          <>
            {rows.map((row, idx) => {
              const key = rowKey(row, idx);
              const expanded = expandedKey === key;
              return (
                <View key={key}>
                  <TouchableOpacity
                    onPress={() => toggleRow(key, row.linked_product_id)}
                    accessibilityRole="button"
                    accessibilityState={{ expanded }}
                    accessibilityLabel={`${row.choice_name ?? 'Opción'}: ${formatQty(row.consumed_qty)} consumidos`}
                    testID={`informes.modificadores.row.${idx}`}
                    style={[
                      styles.reportsDenseListFieldRow,
                      idx > 0 && { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs },
                    ]}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={styles.reportsDenseListFieldLabel}>
                        {row.choice_name ?? 'Opción'}
                        {row.group_name ? ` · ${row.group_name}` : ''}
                      </Text>
                      <Text style={styles.caption} numberOfLines={1}>
                        {row.item_product_name ?? '—'}
                        {row.station_name ? ` · ${row.station_name}` : ''}
                        {row.linked_product_name ? ` · insumo: ${row.linked_product_name}` : ' · sin insumo vinculado'}
                      </Text>
                    </View>
                    <Text style={styles.reportsDenseListFieldValue}>
                      {formatQty(row.consumed_qty)}
                      {(row.unlinked_qty ?? 0) > 0 ? ` (+${formatQty(row.unlinked_qty)} sin stock)` : ''}
                    </Text>
                    <Ionicons
                      name={expanded ? 'chevron-up' : 'chevron-down'}
                      size={16}
                      color={ticketColors.ink3}
                      style={{ marginLeft: spacing.xs }}
                    />
                  </TouchableOpacity>
                  {expanded ? (
                    <View style={{ paddingLeft: spacing.sm }}>
                      {renderDetail(key, row.linked_product_id)}
                    </View>
                  ) : null}
                </View>
              );
            })}
          </>
        ) : (
          <Text style={styles.caption}>Sin consumo de adicionales en el período.</Text>
        )}
      </ReportsDenseListCard>

      {!loading && !loadError && rows.length > 0 ? (
        <TouchableOpacity
          style={[
            styles.secondaryButton,
            { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, alignSelf: 'flex-start', marginTop: spacing.md },
          ]}
          onPress={async () => {
            try {
              await exportModifierConsumptionCsv({
                periodLabel: `${String(from).slice(0, 10)} a ${String(to).slice(0, 10)}`,
                rows,
                reconciliation,
              });
            } catch (err) {
              showError('No se pudo exportar', err instanceof Error ? err.message : 'Reintentá.');
            }
          }}
          accessibilityLabel="Exportar consumo de adicionales a CSV"
          accessibilityRole="button"
          testID="informes.modificadores.export"
        >
          <Ionicons name="download-outline" size={18} color={ticketColors.ink} />
          <Text style={styles.secondaryButtonText}>Exportar CSV</Text>
        </TouchableOpacity>
      ) : null}

      {!loading && !loadError && reconciliation.length > 0 ? (
        <>
          <Text style={[styles.caption, { marginTop: spacing.sm, marginBottom: spacing.xs }]}>
            Reconciliación con movimientos de stock{offCount > 0 ? ` — ${offCount} con diferencia` : ' — todo cuadra'}.
          </Text>
          <ReportsDenseListCard>
            {reconciliation.map((row, idx) => (
              <View
                key={row.linked_product_id ?? idx}
                style={[
                  styles.reportsDenseListFieldRow,
                  idx > 0 && { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs },
                ]}
              >
                <Text style={styles.reportsDenseListFieldLabel}>{row.linked_product_name ?? 'Insumo'}</Text>
                <Text
                  style={[
                    styles.reportsDenseListFieldValue,
                    (row.diff ?? 0) !== 0 && { color: ticketColors.bad },
                  ]}
                >
                  {formatQty(row.moved_qty)} / {formatQty(row.ledger_qty)}
                </Text>
              </View>
            ))}
          </ReportsDenseListCard>
        </>
      ) : null}
    </View>
  );
}
